import { createContext, Dispatch, FC, SetStateAction, useContext, useState } from 'react';
import { useMutation, UseMutationResult, useQuery } from 'react-query';
import { PermissionManagerServiceType } from '../api';
import DialogForm from '../components/DialogForm';
import MainWorkspace from '../components/MainWorkspace';
import {
  FormActionTypeEnum,
  PermissionFormModel,
  RecordPermissionModel,
  StateDialogModel,
} from '../models';
import { permissionsListToRecord } from '../utils/permissionsListToRecord';

type PermissionsManagerContextModel = {
  data: RecordPermissionModel;
  refetch: VoidFunction;
  createMutation: UseMutationResult<any[], unknown, PermissionFormModel, unknown>;
  updateMutation: UseMutationResult<any[], unknown, PermissionFormModel, unknown>;
  removeMutation: UseMutationResult<any, unknown, number, unknown>;
  stateDialog: StateDialogModel;
  setStateDialog: Dispatch<SetStateAction<StateDialogModel>>;
};

const PermissionsManagerContext = createContext<PermissionsManagerContextModel>(
  {} as PermissionsManagerContextModel
);

export const usePermissionsManagerContext = () => {
  const context = useContext(PermissionsManagerContext);
  if (!context) {
    throw new Error('useBillsContext must be used within a BillsManager');
  }
  return context;
};

type PermissionsManagerPropsModel = {
  permissionManagerService: PermissionManagerServiceType;
};

const PermissionsManager: FC<PermissionsManagerPropsModel> = ({ permissionManagerService }) => {
  const [state, setState] = useState<RecordPermissionModel>({});
  const [stateDialog, setStateDialog] = useState<StateDialogModel>({
    actionType: FormActionTypeEnum.ADD,
    open: false,
    defaultValues: { parentPath: '', name: '', code: '', dependenciesApiPermissions: [] },
  });

  const { isLoading, refetch } = useQuery({
    queryKey: 'permissions',
    queryFn: () => permissionManagerService.get(),
    onSuccess: data => setState(permissionsListToRecord(data)),
  });

  /**
   *
   */
  const closeDialog = () => {
    setStateDialog(prevState => ({
      ...prevState,
      actionType: FormActionTypeEnum.ADD,
      open: false,
      defaultValues: {
        parentPath: '',
        name: '',
        code: '',
        dependenciesApiPermissions: [],
      },
    }));
  };

  /**
   *
   */
  const createMutation = useMutation({
    mutationKey: 'AddPermission',
    mutationFn: (formData: PermissionFormModel) =>
      permissionManagerService.create({
        id: formData.id,
        code:
          formData.parentPath !== '' ? `${formData.parentPath}.${formData.code}` : formData.code,
        name: formData.name,
        dependenciesApiPermissions: formData.dependenciesApiPermissions ?? [],
      }),
    onSuccess: () => {
      refetch();
      closeDialog();
    },
  });

  /**
   *
   */
  const updateMutation = useMutation({
    mutationKey: 'updatePermission',
    mutationFn: (formData: PermissionFormModel) =>
      permissionManagerService.update({
        id: formData.id,
        code: formData.parentPath !== '' ? `${formData.parentPath}` : formData.code,
        name: formData.name,
        dependenciesApiPermissions: formData.dependenciesApiPermissions ?? [],
      }),
    onSuccess: () => {
      refetch();
      closeDialog();
    },
  });

  /**
   *
   */
  const removeMutation = useMutation({
    mutationKey: 'RemovePermission',
    mutationFn: (id: number) => permissionManagerService.remove(id),
    onSuccess: () => {
      refetch();
    },
    onError: () => {},
  });

  return (
    <PermissionsManagerContext.Provider
      value={{
        data: state,
        refetch: () => refetch(),
        createMutation,
        updateMutation,
        removeMutation,
        stateDialog,
        setStateDialog,
      }}
    >
      <MainWorkspace />
      {stateDialog.open && (
        <DialogForm
          actionType={stateDialog.actionType}
          open={stateDialog.open}
          handleClose={() => {
            closeDialog();
          }}
          defaultValues={stateDialog.defaultValues}
        />
      )}
    </PermissionsManagerContext.Provider>
  );
};

export default PermissionsManager;
